import deepmerge from 'deepmerge';

import { ThemeColors } from '../../tokens/colors/types';
import { components } from '../../tokens/components/bluestone';
import { shadows } from '../../tokens/shadows/bluestone';
import breakpoints, { mediaQueries, screenMediaQueries } from '../breakpoints';
import {
  BaseDeprecatedColors,
  DeprecatedBrandColors,
  DeprecatedSemanticColors,
  DeprecatedTheme,
} from '../types';
import baseColors from './colors/base/ED';
import accordionItemTokens from './components/accordionItem.tokens';
import alertTokens from './components/alert.tokens';
import createBannerBoxTokens from './components/bannerBox.tokens';
import boxTokens from './components/box.tokens';
import createButtonTokens from './components/button.tokens';
import createCalendarTokens from './components/calendar.tokens';
import cardTokens from './components/card.tokens';
import checkboxTokens from './components/checkbox.tokens';
import decorationLineTokens from './components/decorationLine.tokens';
import drawerHeaderTokens from './components/drawerHeader.tokens';
import drawerHeaderContentTokens from './components/drawerHeaderContent.tokens';
import highlightedHeaderTokens from './components/highlightedHeader.tokens';
import inputTokens from './components/input.tokens';
import createMoneyTokens from './components/money.tokens';
import pillTokens from './components/pill.tokens';
import createPopoverTokens from './components/popover.tokens';
import radioButtonBoxTokens from './components/radioButton.tokens';
import sliderTokens from './components/slider.tokens';
import switchContainerTokens from './components/switchContainer.tokens';
import switchSliderTokens from './components/switchSlider.tokens';
import createTableTokens from './components/table.tokens';
import createTextWithIconTokens from './components/textWithIcon.tokens';
import { space as baseSpace } from './spaces/base';
import { deprecatedSpace } from './spaces/deprecated';

const colorsBrand = { 0: '#E8ECFB', 1: '#1E44D5', 2: '#003399', 3: '#002C52' };

const colorGreyLight = '#F2F2F2';
const colorsGrey = {
  0: '#272727',
  1: '#4C4C4C',
  2: '#6F6F6F',
  3: '#909090',
  4: '#B2B2B2',
  5: '#CBCBCB',
  6: '#E6E6E6',
  7: colorGreyLight,
  8: '#F5F5F5',
  light: colorGreyLight,
  default: '#888',
};

const colorsBlue = [
  '#D4EAF7',
  '#3398d9',
  '#1E44D5',
  '#003399',
  '#002a4f',
  '#000e2a',
  '#366080',
];

const uniqueBackground = '#0A8EFF';
const uniqueForeground = '#1D44D5';

const colorsGreen = { 0: '#1CB670', 1: '#3D8F5D', light: '#E6F5E6' };

const colorsOrange = { 0: '#FF9600', 1: '#DC5F0B', light: '#FFEACC' };

const colorsRed = ['#FDE7EA', '#F01C3D', '#9B0000'];

const deprecatedAppBannerColors = '#198cfb';
const brandSecondary = '#FFCC00';
const rating = '#FFCC00';
const colors: ThemeColors &
  BaseDeprecatedColors &
  DeprecatedSemanticColors &
  DeprecatedBrandColors = deepmerge(
  {
    brandPrimary: colorsBrand,
    brandSecondary,
    deprecatedAppBannerColors,
    appBanner: {
      bg: '#f9f9f7',
      bt: brandSecondary,
      image: null,
      uniqueBackground,
      uniqueForeground,
    },
    banner: {
      bg: colorsBlue[4],
      fg: brandSecondary,
      gradient: ['#002C52', '#000D29'],
    },
    neutrals: colorsGrey,
    positive: {
      light: colorsGreen.light,
      default: colorsGreen[0],
      dark: colorsGreen[1],
    },
    informative: {
      light: colorsBlue[0],
      default: colorsBlue[1],
      dark: colorsBlue[2],
      darkest: colorsBlue[6],
    },
    warning: {
      light: colorsOrange.light,
      default: colorsOrange[0],
      dark: colorsOrange[1],
    },
    negative: {
      light: colorsRed[0],
      default: colorsRed[1],
      dark: colorsRed[2],
    },
    critical: {
      light: colorsRed[0],
      default: colorsRed[1],
      dark: colorsRed[2],
    },
    rating: rating,
    black: '#000',
    white: '#fff',
    advertisementBgColor: baseColors.primary['50'],
    modalWrapperBackgroundColor: 'rgba(39, 39, 39, 0.6)',
    histogramDisabledBackgroundColor: 'rgba(203,203,203,0.3)',
    trainProductColor: '#5335B6',
    trainProductBgColor: baseColors.purple['20'],
    flightProductColor: '#4F8EFF',
    flightProductBgColor: baseColors.blue['20'],
    lightButtonBgColor: colorsBrand[0],
    darkButtonTextColor: colorsBlue[3],
    promotional: {
      primary: {
        default: colorsBrand[3],
        dark: '#000D29',
      },
      secondary: {
        default: '#FFD633',
        dark: '#8F7200',
      },
    },
  },
  baseColors
);

const sizes = [16, 24, 32, 64, 128, 256];

const space = {
  ...baseSpace,
  ...deprecatedSpace,
};

const lineHeights = {
  body: 1.5,
  bodies: {
    base: '24px',
    small: '21px',
    xSmall: '18px',
  },
  heading: 1.25,
  headings: {
    1: '38px',
    2: '30px',
    3: '24px',
    4: '22px',
    5: '20px',
    6: '18px',
  },
  display: {
    1: '48px',
    2: '42px',
  },
  overline: '12px',
};

const decorationLine = decorationLineTokens({
  brandSecondary,
  colorsGrey,
  colors,
});
const popover = createPopoverTokens({});

const radii = {
  small: '4px',
  regular: '8px',
  big: '16px',
  full: '9999px',
};

const fontSizeBody = [12, 14, 16, 18];
const fontSizes = {
  body: fontSizeBody,
  bodies: {
    base: 16,
    small: 14,
    xSmall: 12,
    xxSmall: 10,
  },
  heading: [20, 24, 32],
  headings: {
    1: 32,
    2: 24,
    3: 20,
    4: 18,
    5: 16,
    6: 14,
  },
  display: {
    1: 44,
    2: 40,
  },
  overline: 12,
};
const fontWeights = {
  normal: 400,
  medium: 500,
  bold: 600,
};
const borders = [0, '1px solid', '2px solid', '4px solid'];
const borderWidths = [0, 1, 2, 4, 8, 12];
const iconSizes = {
  tiny: 12,
  small: 16,
  base: 20,
  medium: 24,
  large: 32,
  xlarge: 64,
};

const drawerHeader = drawerHeaderTokens({
  colorsBrand,
  colors,
  borders,
  space,
});

const drawerHeaderContent = drawerHeaderContentTokens({ borders, colors });

const button = createButtonTokens({
  colors,
  colorsBrand,
  radii,
  borders,
  space,
  fontSizeBody,
});

const box = boxTokens({ colors });

const card = cardTokens({ radii });

const highlightedHeader = highlightedHeaderTokens(fontSizes);

const pill = pillTokens({ colors, colorsGrey });

const alert = alertTokens({ colors, borders });

const switchSlider = switchSliderTokens({ colors, space });
const switchContainer = switchContainerTokens({ space });

const slider = sliderTokens({
  colors,
  colorsBrand,
  sizes,
  space,
  fontSizeBody,
});

const checkbox = checkboxTokens({
  borders,
  colors,
  colorsBrand,
  space,
  lineHeights,
  fontSizeBody,
});

const radioButton = radioButtonBoxTokens({
  borders,
  colors,
  colorsBrand,
  lineHeights,
  fontSizeBody,
  fontWeights,
});

const accordionItem = accordionItemTokens({
  borders,
  colors,
  space,
  fontSizeBody,
  fontWeights,
  iconSizes,
});

const textWithIcon = createTextWithIconTokens({ colors, fontSizes, iconSizes });
const money = createMoneyTokens({ fontSizes, fontWeights, colors });
const bannerBox = createBannerBoxTokens({ radii });
const table = createTableTokens({ borderWidths });
const input = inputTokens({ fontSizes, colors });
const calendar = createCalendarTokens({ colors });

const theme: DeprecatedTheme = {
  version: 'blueStone',
  colors: {
    ...colors,
    text: colors,
    button: button.colors,
    drawerHeader: drawerHeader.colors,
    box: box.colors,
    decorationLine: decorationLine.colors,
    drawerHeaderContent: drawerHeaderContent.colors,
    pill: pill.colors,
    alert: alert.colors,
    switchSlider: switchSlider.colors,
    slider: slider.colors,
    checkbox: checkbox.colors,
    radioButton: radioButton.colors,
    brandedProgressBar: brandSecondary,
    textWithIcon: textWithIcon.color,
    accordionItem: accordionItem.colors,
    money: money.colors,
    input: input.colors,
    calendar: calendar.colors,
  },
  sizes: {
    ...sizes,
    decorationLine: decorationLine.sizes,
    switchContainer: switchContainer.sizes,
    switchSlider: switchSlider.sizes,
    slider: slider.sizes,
    checkbox: checkbox.sizes,
    radioButton: radioButton.sizes,
    drawerHeader: drawerHeader.sizes,
    input: input.sizes,
    popover: popover.sizes,
  },
  shadows,
  breakpoints,
  mediaQueries,
  screenMediaQueries,
  space: {
    ...space,
    button: button.space,
    drawerHeader: drawerHeader.space,
    decorationLine: decorationLine.space,
    switchSlider: switchSlider.space,
    slider: slider.space,
    checkbox: checkbox.space,
    accordionItem: accordionItem.space,
    popover: popover.space,
    input: input.space,
  },
  fonts: {
    body: "'Rubik', -apple-system, '.SFNSText-Regular', 'San Francisco', 'Segoe UI', 'Helvetica Neue', 'Lucida Grande', sans-serif",
  },
  fontSizes: {
    ...fontSizes,
    button: button.fontSizes,
    slider: slider.fontSizes,
    checkbox: checkbox.fontSizes,
    radioButton: radioButton.fontSizes,
    textWithIcon: textWithIcon.fontSize,
    accordionItem: accordionItem.fontSizes,
    highlightedHeader: highlightedHeader.fontSizes,
    money: money.fontSizes,
    input: input.fontSizes,
  },
  fontWeights: {
    ...fontWeights,
    button: {
      default: fontWeights.medium,
    },
    accordionItem: accordionItem.fontWeights,
    radioButton: radioButton.fontWeights,
    money: money.fontWeights,
  },
  lineHeights: {
    ...lineHeights,
    checkbox: checkbox.lineHeight,
    radioButton: radioButton.lineHeight,
    highlightedHeader: highlightedHeader.lineHeights,
    input: input.lineHeight,
    button: button.lineHeights,
  },
  letterSpacings: {
    normal: 'normal',
    tracked: '0.1em',
    tight: '-0.05em',
    mega: '0.25em',
    extended: '0.03em',
    condensed: '-0.03em',
    bitCondensed: '-0.02em',
  },
  borders: {
    ...borders,
    button: button.borders,
    drawerHeader: drawerHeader.borders,
    drawerHeaderContent: drawerHeaderContent.borders,
    checkbox: checkbox.borders,
    radioButton: radioButton.borders,
    accordionItem: accordionItem.borders,
    alert: alert.borders,
  },
  borderWidths: {
    ...borderWidths,
    table: table.borderWidths,
  },
  borderStyles: {
    table: table.borderStyles,
  },
  radii: {
    ...radii,
    0: 0,
    1: 4,
    2: 8,
    3: 12,
    4: 16,
    5: '50%',
    6: '100%',
    7: '9999px',
    button: button.radii,
    card: card.radii,
    cardRadii: card.sizesRadii,
    bannerBox: bannerBox.radii,
  },
  iconSizes: {
    ...iconSizes,
    textWithIcon: textWithIcon.iconSize,
    accordionItem: accordionItem.iconSizes,
  },
  inputWidths: {
    full: '100%',
    xxxl: 380,
    xxl: 300,
    xl: 270,
    l: 180,
    m: 100,
    s: 65,
    xs: 50,
  },
  inputHeights: {
    sm: 30,
    m: 40,
    l: 56,
  },
  zIndices: {
    popup: 100,
    tooltip: 200,
    popover: 300,
    stickyBox: 400,
    modal: 500,
  },
  transition: {
    button: button.transition,
    slider: slider.transition,
  },
  transform: {
    button: button.transform,
  },
  opacity: {
    button: button.opacity,
    checkbox: checkbox.opacity,
  },
  components,
};

export default theme;
